/**
 * usePWAInstall
 *
 * - Intercepts `beforeinstallprompt` so the browser NEVER shows the
 *   automatic banner/popup again.
 * - Exposes `canInstall` (boolean) and `install()` (async function).
 * - `install()` triggers the native install dialog and resolves with
 *   'accepted' | 'dismissed'.
 * - Works for Android Chrome, Edge, Samsung Internet, and desktop Chrome.
 * - On iOS Safari the prompt API doesn't exist; `canInstall` stays false.
 *   You can separately detect iOS and show a manual "Add to Home Screen" tip.
 */
import { useState, useEffect, useRef } from 'react';

export function usePWAInstall() {
    const deferredPrompt = useRef(null);
    const [canInstall, setCanInstall] = useState(false);
    const [isInstalled, setIsInstalled] = useState(false);

    useEffect(() => {
        // Already installed (standalone mode)?
        if (window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone) {
            setIsInstalled(true);
            return;
        }

        const onPrompt = (e) => {
            // Suppress the automatic browser popup
            e.preventDefault();
            deferredPrompt.current = e;
            setCanInstall(true);
        };

        const onInstalled = () => {
            setIsInstalled(true);
            setCanInstall(false);
            deferredPrompt.current = null;
        };

        window.addEventListener('beforeinstallprompt', onPrompt);
        window.addEventListener('appinstalled', onInstalled);
        return () => {
            window.removeEventListener('beforeinstallprompt', onPrompt);
            window.removeEventListener('appinstalled', onInstalled);
        };
    }, []);

    const install = async () => {
        if (!deferredPrompt.current) return null;
        deferredPrompt.current.prompt();
        const { outcome } = await deferredPrompt.current.userChoice;
        if (outcome === 'accepted') {
            setIsInstalled(true);
            setCanInstall(false);
        }
        deferredPrompt.current = null;
        return outcome; // 'accepted' | 'dismissed'
    };

    return { canInstall, isInstalled, install };
}
